import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useOutletContext, useSearchParams } from "react-router-dom";
import api from "../../chat/api";
import useProjectsStore from "../../../store/useProjectsStore";
import CreateProjectModal from "./CreateProjectModal";
import AccessRequestsModal from "./AccessRequestsModal";
import { PlusIcon } from "./MyProjectIcons";
import ProjectDataTable from "./ProjectDataTable";
import Pagination from "../../../components/Pagination/Pagination";
import { matchesDateRange, emptyDateFilter } from "./projectDateUtils";
import useIsKbReviewer from "../../../utils/useIsKbReviewer";
import { showWarningToast } from "../../../utils/toast";
import useProjectRowActions from "./useProjectRowActions";
import {
  isSelectableRow,
  matchesSource,
  PROJECT_SOURCES,
} from "./projectAccess";

// v2: the default source changed from "all" to "mine"; a cached v1 value would
// override the new default and quietly undo it.
const FILTER_CACHE_KEY = "myprojects_filters_v2";
const SEARCH_CACHE_KEY = "myprojects_search_v1";

const readCache = (key, fallback = null) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const writeCache = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore quota / private-mode errors
  }
};

const defaultFilters = () => ({
  source: PROJECT_SOURCES.ALL,
  statuses: [],
  bidEndDate: emptyDateFilter(),
  dateSubmitted: emptyDateFilter(),
});

// ─── usePersistedFilters ──────────────────────────────────────────────────────
const usePersistedFilters = (outletSearchQuery, outletActiveFilters) => {
  const [effectiveSearch, setEffectiveSearch] = useState(() => readCache(SEARCH_CACHE_KEY, ""));
  const [effectiveFilters, setEffectiveFilters] = useState(() => readCache(FILTER_CACHE_KEY, defaultFilters()));
  const outletSettled = useRef(false);

  useEffect(() => {
    if (!outletSettled.current) {
      outletSettled.current = true;
      return;
    }
    setEffectiveSearch(outletSearchQuery ?? "");
    writeCache(SEARCH_CACHE_KEY, outletSearchQuery ?? "");
  }, [outletSearchQuery]);

  useEffect(() => {
    if (!outletSettled.current) return;
    setEffectiveFilters(outletActiveFilters ?? defaultFilters());
    writeCache(FILTER_CACHE_KEY, outletActiveFilters ?? defaultFilters());
  }, [outletActiveFilters]);

  return { effectiveSearch, effectiveFilters };
};

const MyProjects = () => {
  const { searchQuery, activeFilters, columnPreferences } = useOutletContext();
  const { effectiveSearch, effectiveFilters } = usePersistedFilters(searchQuery, activeFilters);
  // Reviewers in an invited workspace are read-only — they can't create projects.
  const isReviewer = useIsKbReviewer();

  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = useState(false);

  // ── Zustand: read projects + loading from the shared store instead of useState.
  // Each line "subscribes" to one piece of the store; this component re-renders
  // only when that specific piece changes.
  const projects = useProjectsStore((state) => state.projects);
  const loading = useProjectsStore((state) => state.loading);
  const fetchProjects = useProjectsStore((state) => state.fetchProjects);
  const partialFailure = useProjectsStore((state) => state.partialFailure);

  const [selectedProjects, setSelectedProjects] = useState([]);
  const [selectedProjectKeys, setSelectedProjectKeys] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [headerPortalNode, setHeaderPortalNode] = useState(null);
  // How many people are waiting on access to projects THIS user owns. Drives
  // the header badge; the bell notification is the other half of the signal.
  const [incomingRequests, setIncomingRequests] = useState(0);
  const [isAccessRequestsOpen, setIsAccessRequestsOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [footerPortalNode, setFooterPortalNode] = useState(null);

  // The access request email links here with ?access_requests=1.
  useEffect(() => {
    if (searchParams.get("access_requests") !== "1") return;
    setIsAccessRequestsOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete("access_requests");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const [toolbarNode, setToolbarNode] = useState(null);

  useEffect(() => {
    setHeaderPortalNode(document.getElementById("projects-header-portal"));
    setFooterPortalNode(document.getElementById("projects-footer-portal"));
  }, []);

  useEffect(() => {
    let injectionNode = document.getElementById('dynamic-bulk-action-portal');
    if (!injectionNode) {
      injectionNode = document.createElement('div');
      injectionNode.id = 'dynamic-bulk-action-portal';
      injectionNode.style.display = 'none';
      injectionNode.style.width = '100%';
      injectionNode.style.justifyContent = 'flex-end';
      injectionNode.style.alignItems = 'center';
      
      const searchInputs = document.querySelectorAll('input');
      const searchInput = Array.from(searchInputs).find(i => i.placeholder?.toLowerCase().includes('search'));
      
      if (searchInput) {
        let container = searchInput.parentElement;
        let found = false;
        
        while (container && container.tagName !== 'BODY') {
          const textContent = container.textContent.toLowerCase();
          if (textContent.includes('filter') && textContent.includes('columns')) {
            found = true;
            break;
          }
          container = container.parentElement;
        }
        
        if (found && container) {
          container.appendChild(injectionNode);
          setToolbarNode(injectionNode);
        } else {
          let fallbackContainer = searchInput.parentElement;
          while (fallbackContainer && fallbackContainer.children.length < 2 && fallbackContainer.tagName !== 'BODY') {
            fallbackContainer = fallbackContainer.parentElement;
          }
          if (fallbackContainer) {
             fallbackContainer.appendChild(injectionNode);
             setToolbarNode(injectionNode);
          }
        }
      }
    } else {
      setToolbarNode(injectionNode);
    }
  }, []);

  useEffect(() => {
    if (!toolbarNode) return;
    const container = toolbarNode.parentElement;
    if (!container) return;

    if (selectedProjects.length > 0) {
      toolbarNode.style.display = 'flex';
      Array.from(container.children).forEach(child => {
        if (child !== toolbarNode) {
          if (!child.hasAttribute('data-orig-display')) {
            child.setAttribute('data-orig-display', child.style.display || '');
          }
          child.style.display = 'none';
        }
      });
    } else {
      toolbarNode.style.display = 'none';
      Array.from(container.children).forEach(child => {
        if (child !== toolbarNode && child.hasAttribute('data-orig-display')) {
          child.style.display = child.getAttribute('data-orig-display') || '';
          child.removeAttribute('data-orig-display');
        }
      });
    }

    return () => {
      if (selectedProjects.length > 0 && container) {
        Array.from(container.children).forEach(child => {
          if (child !== toolbarNode && child.hasAttribute('data-orig-display')) {
            child.style.display = child.getAttribute('data-orig-display') || '';
            child.removeAttribute('data-orig-display');
          }
        });
      }
    };
  }, [selectedProjects.length, toolbarNode]);

  // `fetchProjects` now lives in the Zustand store (see top of file). Its
  // reference is stable, so this effect runs once on mount — same as before.
  // Runs as a layout effect so `loading` flips to true BEFORE the browser
  // paints — otherwise the store's cached projects from a previous visit
  // would flash for one frame before the skeleton appears.
  useLayoutEffect(() => {
    fetchProjects();
    // Refetch when the user switches workspace from the global sidebar.
    const onWorkspaceChange = () => fetchProjects();
    window.addEventListener("workspace-changed", onWorkspaceChange);
    return () => window.removeEventListener("workspace-changed", onWorkspaceChange);
  }, [fetchProjects]);

  // The pending-request count for the header badge. Cheap and indexed, and a
  // failure here must never take the Projects page down with it — the badge
  // just stays hidden.
  const refreshIncomingRequests = useCallback(async () => {
    try {
      const res = await api.get("/api/v1/rfp/access-requests/?box=incoming&status=pending");
      setIncomingRequests((res.data?.requests || []).length);
    } catch {
      setIncomingRequests(0);
    }
  }, []);

  useEffect(() => {
    refreshIncomingRequests();
    const onWorkspaceChange = () => refreshIncomingRequests();
    window.addEventListener("workspace-changed", onWorkspaceChange);
    return () => window.removeEventListener("workspace-changed", onWorkspaceChange);
  }, [refreshIncomingRequests]);


  const filteredProjects = useMemo(() => {
    let result = projects;

    if (effectiveSearch) {
      const query = effectiveSearch.toLowerCase().trim();
      result = result.filter(
        (project) =>
          (project.name?.toLowerCase() || "").includes(query) ||
          (project.description?.toLowerCase() || "").includes(query) ||
          // Carried over from the old Shared With Me tab, which let you find a
          // project by who sent it. Owner matches the same idea for owned rows.
          (project.shared_by?.toLowerCase() || "").includes(query) ||
          (project.owner?.name?.toLowerCase() || "").includes(query)
      );
    }

    // Which slice the source switcher is on. Defaults to the user's own work,
    // so an admin's list is not buried under members' read-only projects.
    result = result.filter((p) => matchesSource(p, effectiveFilters?.source));

    if (effectiveFilters?.statuses?.length > 0) {
      const allowedStatuses = effectiveFilters.statuses.map(s => s.toLowerCase());
      result = result.filter(p => allowedStatuses.includes((p.status || "").toLowerCase()));
    }

    // Date-range filters (Bid End Date / Date Submitted). A "now" snapshot keeps
    // the relative presets (Last Week/Month/Year) stable across the pass.
    const now = new Date();
    if (effectiveFilters?.bidEndDate) {
      result = result.filter(p => matchesDateRange(p.deadline, effectiveFilters.bidEndDate, now));
    }
    if (effectiveFilters?.dateSubmitted) {
      result = result.filter(p => matchesDateRange(p.date_submitted, effectiveFilters.dateSubmitted, now));
    }

    return result;
  }, [projects, effectiveSearch, effectiveFilters]);

  // The only bulk action is Delete, which the backend allows for owners alone -
  // so projects shared with the user are never selectable.
  const selectableProjects = useMemo(
    () => filteredProjects.filter(isSelectableRow),
    [filteredProjects]
  );

  const totalPages = Math.max(1, Math.ceil(filteredProjects.length / rowsPerPage));

  // Filtering, searching, leaving or deleting can shrink the list below the page
  // the user is standing on, which would render an empty table with no way back.
  // Snap to the last real page whenever that happens.
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const currentProjects = useMemo(() => {
    const page = Math.min(currentPage, totalPages);
    const indexOfLastProject = page * rowsPerPage;
    const indexOfFirstProject = indexOfLastProject - rowsPerPage;
    return (Array.isArray(filteredProjects) ? filteredProjects : []).slice(
      indexOfFirstProject,
      indexOfLastProject
    );
  }, [filteredProjects, currentPage, totalPages, rowsPerPage]);

  // "Select all" means the owned rows on the page being looked at — never rows on
  // other pages. It used to select every page, so a bulk delete removed projects
  // the user had never seen.
  const pageSelectableProjects = useMemo(
    () => currentProjects.filter(isSelectableRow),
    [currentProjects]
  );

  const isAllChecked = useMemo(() => {
    return (
      pageSelectableProjects.length > 0 &&
      pageSelectableProjects.every((p) => selectedProjects.includes(p.id))
    );
  }, [selectedProjects, pageSelectableProjects]);

  // Drop selections whose project is no longer visible — a refetch, a filter
  // change or another user's delete can otherwise leave invisible rows selected
  // and the bulk toolbar stuck on screen.
  useEffect(() => {
    const selectableIds = new Set(selectableProjects.map((p) => p.id));
    setSelectedProjects((prev) => {
      const next = prev.filter((id) => selectableIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [selectableProjects]);

  // One warning per incomplete load, so the user knows the table is missing
  // rows rather than assuming those projects are gone.
  useEffect(() => {
    if (partialFailure) {
      showWarningToast(`Couldn't load ${partialFailure}. Some projects may be missing.`, {
        id: "projects-partial-load",
      });
    }
  }, [partialFailure]);

  const paginationUI = useMemo(() => {
    if (filteredProjects.length === 0) return null;
    return (
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={filteredProjects.length}
        rowsPerPage={rowsPerPage}
        onPageChange={setCurrentPage}
        onRowsPerPageChange={setRowsPerPage}
      />
    );
  }, [filteredProjects.length, currentPage, totalPages, rowsPerPage]);

  const createProjectButton = useMemo(() => {
    return (
      <div className="flex items-center gap-3">
        {incomingRequests > 0 && (
          <button
            onClick={() => setIsAccessRequestsOpen(true)}
            title="People asking for access to your projects"
            className="flex items-center gap-2 px-4 py-2 rounded-md border border-[#C95EBE] text-[#C95EBE] text-[14px] font-semibold hover:bg-[#C95EBE]/5 transition-colors"
          >
            Access requests
            <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-[#C95EBE] text-white text-[12px] font-bold inline-flex items-center justify-center">
              {incomingRequests}
            </span>
          </button>
        )}
        {!isReviewer && (
          <button
            data-tour="create-project"
            className="flex items-center gap-2 px-5 py-2 bg-[#A04A97] rounded-md text-white text-[14px] hover:bg-[#8a3f83] transition-colors"
            onClick={() => setIsCreateProjectModalOpen(true)}
          >
            <PlusIcon />
            <span className="font-semibold">Create Project</span>
          </button>
        )}
      </div>
    );
  }, [isReviewer, incomingRequests]);

  const handleSelectAll = () => {
    if (isAllChecked) {
      setSelectedProjects([]);
      setSelectedProjectKeys([]);
    } else {
      setSelectedProjects(pageSelectableProjects.map((item) => item.id));
      setSelectedProjectKeys(pageSelectableProjects.map((item) => item.key).filter(Boolean));
    }
  };

  const handleCheckboxChange = (id, key) => {
    if (!id) return;
    // Selection feeds the owner-only bulk Delete, so refuse anything the user
    // doesn't own even if a stale render somehow offered the checkbox.
    if (!projects.some((p) => p.id === id && isSelectableRow(p))) return;
    if (selectedProjects.includes(id)) {
      setSelectedProjects(selectedProjects.filter((projectId) => projectId !== id));
      setSelectedProjectKeys(selectedProjectKeys.filter((projectKey) => projectKey !== key));
    } else {
      setSelectedProjects([...selectedProjects, id]);
      setSelectedProjectKeys([...selectedProjectKeys, key]);
    }
  };

  const { tableProps, modals, showPopup, closeActionMenu, openDeleteConfirm, closeDeleteConfirm } = useProjectRowActions({
    selectedIds: selectedProjects,
    onSelectionCleared: useCallback(() => {
      setSelectedProjects([]);
      setSelectedProjectKeys([]);
    }, []),
  });

  const handleBulkDelete = () => {
    if (!selectedProjects.length) return showPopup("Error", "Please select items to delete.");
    closeActionMenu();
    openDeleteConfirm();
  };

  // "Nothing here" has two very different causes, and using one message for both
  // sends people hunting for a filter when they simply have no projects.
  const emptyState = useMemo(
    () =>
      projects.length === 0
        ? {
            title: "No projects yet",
            body: "Create your first project to get started. Your projects will show up here once added.",
          }
        : {
            title: "No matching projects",
            body: "No project matches your current search and filters. Try clearing them to see everything again.",
          },
    [projects.length]
  );

  const bulkActionsContent = (
    <div className="flex items-center justify-end gap-3 w-full">
      <button
        onClick={handleBulkDelete}
        className="flex items-center justify-center gap-1 px-[14px] py-2 rounded-full border border-transparent bg-red-100 hover:bg-red-200 disabled:opacity-60 transition"
      >
        <img src="/delete-icon-red.svg" alt="Delete" className="w-4 h-4" />
        <span className="text-[#E21919]/75 font-outfit text-sm font-medium leading-none">
          Delete
        </span>
      </button>
      <button
        onClick={() => {
          setSelectedProjects([]);
          closeDeleteConfirm();
        }}
        className="flex items-center justify-center gap-1 p-2 rounded-full border border-black/10 bg-[#ECECEC] transition hover:bg-[#e0e0e0]"
      >
        <img src="/cancel_icon.svg" alt="Cancel" className="w-5 h-5 min-w-[20px]" />
      </button>
    </div>
  );

  return (
    <>
      <div className="max-w-full overflow-x-hidden flex flex-col h-full bg-white pt-2">
        <div className="flex flex-col flex-1 overflow-hidden w-full pl-2 pr-1 pb-2">
          <div className="flex-1 flex flex-col w-[99%] ml-1 overflow-hidden bg-white border border-black/10 rounded-lg">
            <div className="flex-1 overflow-auto custom-scrollbar">
              <ProjectDataTable
                {...tableProps}
                columnPreferences={columnPreferences}
                isAllChecked={isAllChecked}
                handleSelectAll={handleSelectAll}
                selectableCount={pageSelectableProjects.length}
                filteredProjects={filteredProjects}
                loading={loading}
                currentProjects={currentProjects}
                selectedProjects={selectedProjects}
                handleCheckboxChange={handleCheckboxChange}
                emptyState={emptyState}
              />
            </div>
          </div>
        </div>

        {/* --- MODALS --- */}
        {modals}

        <CreateProjectModal
          isOpen={isCreateProjectModalOpen}
          onClose={() => { setIsCreateProjectModalOpen(false); fetchProjects(true); }}
          onCreateProject={async () => {}}
        />

        <AccessRequestsModal
          isOpen={isAccessRequestsOpen}
          onClose={() => setIsAccessRequestsOpen(false)}
          onResolved={() => {
            refreshIncomingRequests();
            fetchProjects(true);
          }}
        />
      </div>

      {/* --- PORTALS --- */}
      {headerPortalNode && createPortal(createProjectButton, headerPortalNode)}
      {footerPortalNode && createPortal(paginationUI, footerPortalNode)}
      {toolbarNode && selectedProjects.length > 0 && createPortal(bulkActionsContent, toolbarNode)}
    </>
  );
};

export default MyProjects;
